import { useEffect, useState } from 'react'
import './SettingsModal.css'

const PROVIDERS = [
  { id: 'openai', name: 'OpenAI' },
  { id: 'gemini', name: 'Google Gemini' },
  { id: 'ollama', name: 'Ollama (Local)' },
]

export const LlmBotEditor = ({ bot, onSave, onCancel, onDelete, onStartChat }: any) => {
  const isNew = !bot
  const [displayName, setDisplayName] = useState('')
  const [description, setDescription] = useState('')
  const [provider, setProvider] = useState('')
  const [model, setModel] = useState('')
  const [apiKey, setApiKey] = useState('')
  const [systemPrompt, setSystemPrompt] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (bot) {
      setDisplayName(bot.displayName || '')
      setDescription(bot.description || '')
      setProvider(bot.llm?.provider || '')
      setModel(bot.llm?.model || '')
      setApiKey(bot.llm?.apiKey || '')
      setSystemPrompt(bot.role?.systemPrompt || '')
    } else {
      setDisplayName('')
      setDescription('')
      setProvider('')
      setModel('')
      setApiKey('')
      setSystemPrompt('')
    }
  }, [bot])

  const handleSave = async () => {
    if (!displayName.trim()) {
      setError('Name is required')
      return
    }
    setIsSaving(true)
    setError('')
    try {
      await onSave({
        ...(bot ? { botId: bot.botId } : {}),
        displayName: displayName.trim(),
        description: description.trim(),
        llm: {
          provider: provider || null,
          model: model || null,
          apiKey: apiKey || null,
          workerPublicKey: null,
        },
        role: {
          systemPrompt: systemPrompt.trim() || null,
        },
      })
    } catch (err: any) {
      setError(err.message || 'Failed to save')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="settings-panel" data-testid="bot-editor">
      <div className="settings-content">
        <div className="settings-section">
          <h3>{isNew ? 'New Bot' : 'Edit Bot'}</h3>
          <div className="bot-config-field">
            <label className="bot-config-label">Name</label>
            <input
              className="provider-input"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              placeholder="Code Reviewer"
            />
          </div>
          <div className="bot-config-field" style={{ marginTop: 12 }}>
            <label className="bot-config-label">Description</label>
            <input
              className="provider-input"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Brief description"
            />
          </div>
        </div>

        <div className="settings-section">
          <h3>LLM</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="bot-config-field">
              <label className="bot-config-label">Provider</label>
              <select
                className="provider-input"
                value={provider}
                onChange={e => setProvider(e.target.value)}
              >
                <option value="">Auto</option>
                {PROVIDERS.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="bot-config-field">
              <label className="bot-config-label">Model</label>
              <input
                className="provider-input"
                value={model}
                onChange={e => setModel(e.target.value)}
                placeholder="gpt-4.1, gemini-2.5-flash, etc."
              />
            </div>
            {provider !== 'ollama' && (
              <div className="bot-config-field">
                <label className="bot-config-label">API Key</label>
                <input
                  className="provider-input"
                  type="password"
                  value={apiKey}
                  onChange={e => setApiKey(e.target.value)}
                  placeholder="sk-..."
                />
              </div>
            )}
          </div>
        </div>

        <div className="settings-section">
          <h3>System Prompt</h3>
          <textarea
            className="provider-input bot-config-textarea"
            value={systemPrompt}
            onChange={e => setSystemPrompt(e.target.value)}
            placeholder="You are a helpful assistant specialized in..."
            rows={5}
            style={{ resize: 'vertical', minHeight: 80, fontFamily: 'inherit', lineHeight: 1.5 }}
          />
        </div>

        {error && <div className="settings-error">{error}</div>}
      </div>

      <div className="settings-footer">
        {!isNew && onDelete && (
          <button
            className="cancel-button"
            style={{
              color: 'var(--color-error)',
              borderColor: 'var(--color-error)',
              marginRight: 'auto',
            }}
            onClick={() => onDelete(bot.botId)}
          >
            Delete
          </button>
        )}
        {!isNew && onStartChat && (
          <button
            className="save-button"
            style={{ background: 'var(--color-primary)' }}
            onClick={() => onStartChat(bot)}
          >
            Chat
          </button>
        )}
        <button className="cancel-button" onClick={onCancel}>
          Cancel
        </button>
        <button className="save-button" onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  )
}
